import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Loader2, ChevronLeft } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import AlbumCard from '../../components/public/AlbumCard';
import SEO from '../../components/SEO';
import ShareButtons from '../../components/ShareButtons';
import { PUBLIC_BASE } from './config';
import ContentCommentSection from '../../components/public/ContentCommentSection';
import ArtistProfileSection from '../../components/public/ArtistProfileSection';
import LikeButton from '../../components/public/LikeButton';

// ssdMobilenetv1 모델 로드 여부 (한 번만)
let faceModelsLoaded = false;

/** face-api.js ssdMobilenetv1 — 눈·코·입 기반 정확한 얼굴 감지 */
function useFacePosition(imageUrl) {
  const [position, setPosition] = useState('center center');

  useEffect(() => {
    if (!imageUrl) return;

    const detect = async () => {
      try {
        const faceapi = await import('face-api.js');

        if (!faceModelsLoaded) {
          await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
          faceModelsLoaded = true;
        }

        const proxyUrl = `/api/img-proxy?url=${encodeURIComponent(imageUrl)}`;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = proxyUrl;
        });

        const detections = await faceapi.detectAllFaces(
          img,
          new faceapi.SsdMobilenetv1Options({ minConfidence: 0.3 })
        );

        if (detections.length === 0) return;

        // 가장 큰 얼굴 기준
        const box = [...detections]
          .sort((a, b) => b.box.width * b.box.height - a.box.width * a.box.height)[0].box;

        const faceCenterPct = ((box.top + box.height / 2) / img.naturalHeight) * 100;
        // 코 위치가 중앙에 오도록 -5% 오프셋, 10~65% 클램프
        const adjusted = Math.round(Math.min(Math.max(faceCenterPct - 3, 10), 65));
        setPosition(`center ${adjusted}%`);
      } catch (err) {
        console.warn('[FaceDetect]', err.message);
      }
    };

    detect();
  }, [imageUrl]);

  return position;
}

function PublicArtistDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        const res = await axios.get(`/public/artists/${id}`);
        setArtist(res.data);
        setAlbums(res.data.albums || []);
      } catch (err) {
        console.error('아티스트 상세 로드 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArtist();
  }, [id]);

  const bgPosition = useFacePosition(artist?.image);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  if (!artist) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] text-slate-400">
          아티스트를 찾을 수 없습니다.
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title={artist.nameKr || '아티스트'}
        description={`${artist.nameKr} - p!nk 소속 아티스트`}
        path={`/artists/${id}`}
        image={artist.image}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'MusicGroup',
          name: artist.nameKr,
          image: artist.image,
          url: `https://impink.co.kr/artists/${id}`,
        }}
      />
      {/* 아티스트 헤더 */}
      <section className="relative overflow-hidden">
        {/* 블러 배경 */}
        {artist.image && (
          <>
            <div
              className="absolute inset-0 bg-cover scale-110"
              style={{ backgroundImage: `url(${artist.image})`, backgroundPosition: bgPosition }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1526] via-[#0d1526]/80 to-[#0d1526]/40" />
          </>
        )}
        {!artist.image && (
          <div className="absolute inset-0 bg-gradient-to-b from-[#182334] to-[#0d1526]" />
        )}

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 flex items-end gap-8">
          {/* 아티스트 사진 */}
          <div className="hidden sm:block w-36 h-36 md:w-48 md:h-48 rounded-full overflow-hidden shrink-0 shadow-2xl bg-[#182334]">
            {artist.image ? (
              <img
                src={artist.image}
                alt={artist.nameKr}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                <User size={48} className="text-slate-500" />
              </div>
            )}
          </div>

          {/* 아티스트 정보 */}
          <div className="min-w-0">
            <button
              onClick={() => navigate(`${PUBLIC_BASE}/artists`)}
              className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-3 transition-colors">
              <ChevronLeft size={15} />
              아티스트 목록
            </button>
            <p className="text-sky-400 text-xs font-medium tracking-widest uppercase mb-2">
              아티스트
            </p>
            <h1 className="text-white text-3xl md:text-4xl font-bold truncate mb-1">
              {artist.nameKr}
            </h1>
            {artist.nameEn && (
              <p className="text-slate-400 text-base">{artist.nameEn}</p>
            )}
            <div className="flex items-center gap-3 text-slate-400 text-sm mt-2">
              <span>앨범 {albums.length}장</span>
              <LikeButton targetType="artist" targetId={id} />
              <ShareButtons
                title={artist.nameKr}
                description={`${artist.nameKr} - p!nk 소속 아티스트`}
                image={artist.image}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 디스코그래피 */}
      {albums.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="text-white text-xl font-bold mb-6">디스코그래피</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
            {albums.map((album) => (
              <AlbumCard key={album.id} album={album} />
            ))}
          </div>
        </section>
      )}

      <ArtistProfileSection artistId={id} artistName={artist.nameKr} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <ContentCommentSection targetType="artist" targetId={id} label="응원메세지" />
      </div>
    </PublicLayout>
  );
}

export default PublicArtistDetailPage;
